import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Client, CommandStatus, DisconnectControlDataIn, MeterDataOut, PermissionsEnum } from '../../../../api/api-reference';
import { AuthService } from '../../../../services/auth.service';
import { DiagnosticsStateService, DiagnosticsStepResult, toDiagnosticsSteps } from '../../../../services/diagnostics-state.service';
import { RelayStateService } from '../../../../services/relay-state.service';
import { DeviceInformationComponent } from '../device-information/device-information.component';
import { ConnectivityComponent } from '../connectivity/connectivity.component';
import { CommandsComponent } from '../commands/commands.component';
import { TokensComponent } from '../tokens/tokens.component';
import { EventsAlarmsComponent } from '../events-alarms/events-alarms.component';
import { EventLogsComponent } from '../event-logs/event-logs.component';
import { LoadProfileComponent } from '../load-profile/load-profile.component';
import { RunReadModalComponent } from './run-read-modal/run-read-modal.component';
import { DiagnosticsModalComponent } from './diagnostics-modal/diagnostics-modal.component';
import { ConfirmDialogComponent } from '../../../core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-device-details',
  standalone: true,
    imports: [CommonModule, TranslateModule, AngularSvgIconModule, DeviceInformationComponent, ConnectivityComponent, CommandsComponent, TokensComponent, EventsAlarmsComponent, EventLogsComponent, LoadProfileComponent, DiagnosticsModalComponent, RunReadModalComponent, ConfirmDialogComponent],
  templateUrl: './device-details.component.html',
  styleUrl: './device-details.component.scss',
})
export class DeviceDetailsComponent implements OnInit, OnDestroy {
  row: MeterDataOut | undefined = history.state?.row;
  public PermissionsEnum = PermissionsEnum;

  tabs = [
    { id: 'Device Details', labelKey: 'DEVICE_DETAILS' },
    { id: 'Load Profile', labelKey: 'LOAD_PROFILE' },
    { id: 'Connectivity', labelKey: 'CONNECTIVITY' },
    { id: 'Events & Alarms', labelKey: 'EVENTS_ALARMS' },
    { id: 'Commands', labelKey: 'COMMANDS' },
    { id: 'Tokens', labelKey: 'TOKENS' },
  ];
  activeTab = this.tabs[0].id;

  isDiagnosticsModalOpen = false;
  isDiagnosticsConfirmOpen = false;
  isPingConfirmOpen = false;
  isRelayConfirmOpen = false;
  isRunReadConfirmOpen = false;
  // Held from the moment the command is sent until the meter reports the outcome.
  isRelayPending = false;
  diagnosticsStatus?: CommandStatus;
  diagnosticsErrorMessage?: string;
    diagnosticsSteps: DiagnosticsStepResult[] = [];
    isRunReadModalOpen = false;
    runReadCommandId: string | undefined;

  private diagnosticsSubscription?: Subscription;
  private relaySubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private client: Client,
    private toastr: ToastrService,
    private diagnosticsState: DiagnosticsStateService,
    private relayState: RelayStateService,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    // The relay only actually moves once the meter confirms, so the button label follows the
    // command result rather than the request.
    void this.relayState.ensureSubscribed();
    this.relaySubscription = this.relayState.results$.subscribe((message) => {
      if (message.meterId !== this.row?.id) return;

      // Both outcomes are terminal, so either one releases the button.
      this.isRelayPending = false;
      if (message.commandStatus !== CommandStatus.Success) return;

      this.row.isConnected = message.status;
    });

    void this.diagnosticsState.ensureSubscribed();
    this.diagnosticsSubscription = this.diagnosticsState.results$.subscribe((message) => {
      if (message.meterNumber !== this.row?.meterNo) return;

      this.diagnosticsStatus = message.status;
      this.diagnosticsErrorMessage = message.errorMessage;
      this.diagnosticsSteps = message.steps ?? [];
    });

    if (this.authService.isAuthorized([PermissionsEnum.Ondemand_event_log_view])) {
      this.tabs.push({ id: 'Event Logs', labelKey: 'EVENT_LOGS' });
    }

    // deep link to a specific tab via ?tab= - falls back to the default tab if missing/invalid
    const tabParam = this.route.snapshot.queryParamMap.get('tab');
    if (tabParam && this.tabs.some((t) => t.id === tabParam)) {
      this.activeTab = tabParam;
    }

    if (this.row) return;

    // no state (fresh load / pasted link) - fetch by the meterId route param instead
    const meterId = this.route.snapshot.paramMap.get('meterId');
    if (!meterId) return;

    this.client.getDevice(meterId).subscribe({
      next: (device) => {
        const row = new MeterDataOut();
        row.id = device.id;
        row.meterMake = device.meterMakeName;
        row.meterNo = device.meterNumber;
        row.assetTypeName = device.assetTypeName;
        row.dataCollectionIntervals = device.dataCollectionInterval;
        row.status = device.status === 'Online';
        // getDevice reports the last known relay state as a string, "/" when the meter has no snapshot
        row.isConnected = device.connection === '/' ? undefined : device.connection === 'Connected';
        this.row = row;
      },
      error: (err) => this.toastr.error(err),
    });
  }

  ngOnDestroy(): void {
    this.diagnosticsSubscription?.unsubscribe();
    this.relaySubscription?.unsubscribe();
  }

  // A meter with no snapshot yet has no last known relay state - treat that as connected,
  // so the relay can still be opened.
  get isRelayConnected(): boolean {
    return this.row?.isConnected !== false;
  }

  get relayActionKey(): string {
    return this.isRelayConnected ? 'DISCONNECT' : 'CONNECT';
  }

  askToSetRelayState(): void {
    this.isRelayConfirmOpen = true;
  }

  closeRelayConfirm(): void {
    this.isRelayConfirmOpen = false;
  }

  // The relay change is tracked as a command, so the commands tab is where its outcome shows up.
  setRelayState(): void {
    this.isRelayConfirmOpen = false;
    this.isRelayPending = true;

    const data = new DisconnectControlDataIn();
    data.meterNo = this.row!.meterNo;
    data.setConnected = !this.isRelayConnected;
    // The confirm does not ask for a reason, but the endpoint requires one.
    data.reason = 'Manual action';

    this.client.setConnectedState(data).subscribe({
      next: () => {
        this.toastr.success('Relay command sent.');
        this.selectTab('Commands');
      },
      error: (err) => {
        this.isRelayPending = false;
        this.toastr.error(err);
      },
    });
  }

  askToPing(): void {
    this.isPingConfirmOpen = true;
  }

  closePingConfirm(): void {
    this.isPingConfirmOpen = false;
  }

  // The ping has no display of its own, so the commands tab is where its outcome shows up.
  pingMeter(): void {
    this.isPingConfirmOpen = false;

    this.client.pingMeter(this.row!.meterNo).subscribe({
      next: (response) => {
        this.toastr.success(response);
        this.selectTab('Commands');
      },
      error: (err) => this.toastr.error(err),
    });
  }


  askToRunDiagnostics(): void {
    this.isDiagnosticsConfirmOpen = true;
  }

  closeDiagnosticsConfirm(): void {
    this.isDiagnosticsConfirmOpen = false;
  }

  runDiagnostics(): void {
    this.isDiagnosticsConfirmOpen = false;

    this.diagnosticsStatus = CommandStatus.Pending;
    this.diagnosticsErrorMessage = undefined;
    this.diagnosticsSteps = [];
    this.isDiagnosticsModalOpen = true;

    this.client.runDiagnostics(this.row!.meterNo).subscribe({
      next: (response) => {
        this.toastr.success(response);
        // The run shows up as a command, so leave the page on the tab that lists it.
        this.selectTab('Commands');
      },
      error: (err) => {
        this.diagnosticsStatus = undefined;
        this.isDiagnosticsModalOpen = false;
        this.toastr.error(err);
      },
    });
  }

  goBack() {
    this.router.navigate(['/devices/all-devices']);
  }

  askToRunRead(): void {
    this.isRunReadConfirmOpen = true;
  }

  closeRunReadConfirm(): void {
    this.isRunReadConfirmOpen = false;
  }

  runRead() {
    this.isRunReadConfirmOpen = false;

    this.client.runReadLoadProfile(this.row!.id!).subscribe({
      next: (response) => {
        this.runReadCommandId = response.commandId;
        this.isRunReadModalOpen = true;
      },
      error: (err) => this.toastr.error(err),
    });
  }

  closeRunReadModal() {
    this.isRunReadModalOpen = false;
    this.runReadCommandId = undefined;
  }

  selectTab(tab: string) {
    this.activeTab = tab;
    // keep the URL in sync so the current tab is always reflected in a copyable link
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab }, replaceUrl: true });
  }
}
